import { IDENTIFIER_DEFINITIONS } from '@/lib/identifiers'
import { FAMILY_OF_FOUR_SIZE, REFRIGERATOR_LIFESPAN } from '@/lib/refrigerator-answer-facts'

/**
 * Homepage 2026 — the twelve question-and-answer pairs the page renders, as
 * data, so the FAQPage node can be emitted server-side (FRIDGE-784).
 *
 * THE RULE (FRIDGE-380, FRIDGE-640): structured data describes what is on the
 * page — every question and answer here is the VISIBLE text of the canvas
 * section, verbatim, and nothing more. The canvas markup
 * (components/homepage-2026/canvas/sections.tsx, generated from Main.dc.html)
 * is the source; tests/homepage-2026-schema.test.ts extracts the section text
 * and fails the moment either side drifts. Never add a pair here that the page
 * does not render, and never edit a string here without editing the canvas.
 *
 * The eight "Quick answers from Fridge.com" come first (page order), then the
 * four code explainers — FIN, FPN, FFN, FIS, in that order, always.
 */
export interface QuickAnswer {
  question: string
  answer: string
}

/**
 * FRIDGE-1435: the size and lifespan answers are not typed here or in the
 * canvas. Both render lib/refrigerator-answer-facts.ts, the facts /llms.txt and
 * the tool pages print, so the page cannot carry a second number. Each fits the
 * canvas rule of four lines per answer.
 */
export const FAMILY_OF_FOUR_QUICK_ANSWER: QuickAnswer = {
  question: FAMILY_OF_FOUR_SIZE.question,
  answer: FAMILY_OF_FOUR_SIZE.answer,
}

export const LIFESPAN_QUICK_ANSWER: QuickAnswer = {
  question: REFRIGERATOR_LIFESPAN.question,
  answer: REFRIGERATOR_LIFESPAN.shortAnswer,
}

export const HOMEPAGE_QUICK_ANSWERS: readonly QuickAnswer[] = [
  FAMILY_OF_FOUR_QUICK_ANSWER,
  {
    question: 'Is a French door or side-by-side refrigerator better?',
    answer: 'French door: wider shelves and better fresh-food organization. Side-by-side: easier frozen access in narrow kitchens.',
  },
  {
    question: 'When is the best time to buy a refrigerator?',
    answer: 'Price drops cluster around May–July and the winter holidays. A 30-day price history shows whether a price is a real deal.',
  },
  {
    question: 'Are ENERGY STAR refrigerators worth it?',
    answer: 'ENERGY STAR refrigerators typically save $50–$100+ per year on electricity. Local utility rebates can take hundreds more off up front.',
  },
  LIFESPAN_QUICK_ANSWER,
  {
    question: 'What is a counter-depth refrigerator?',
    answer: 'A counter-depth refrigerator is 24–25 inches deep so the doors sit flush with standard counters. Standard-depth models run 30–36 inches deep.',
  },
  {
    question: 'How much does it cost to run a refrigerator?',
    answer: 'A modern full-size refrigerator costs roughly $4–$15 per month to run, depending on size, age, and local electricity rates.',
  },
  {
    question: 'What temperature should a refrigerator be set to?',
    answer: 'Keep the refrigerator at 35–38°F and the freezer at 0°F. Above 40°F, bacteria growth accelerates — that is the food-safety line.',
  },
] as const

/** The four code explainers — FIN, FPN, FFN, FIS, in that order (owner ruling 08/17/2026). */
export const HOMEPAGE_CODE_ANSWERS: readonly QuickAnswer[] = [
  { question: 'What is a FIN?', answer: IDENTIFIER_DEFINITIONS.FIN },
  { question: 'What is an FPN?', answer: IDENTIFIER_DEFINITIONS.FPN },
  { question: 'What is an FFN?', answer: IDENTIFIER_DEFINITIONS.FFN },
  { question: 'What is FIS?', answer: IDENTIFIER_DEFINITIONS.FIS },
] as const
